import type { Key } from "react-stately";

import { USER_ROLES } from "@nlc/shared-types";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { UserRole } from "@/features/auth/permissions/config";

import { Banner } from "@/components/banner";
import { RequestsKickstarterBanner } from "@/components/kickstarter-banner/banners.tsx";
import { Loader } from "@/components/Loader";
import { PauseBanner } from "@/components/pause-banner";
import { SearchField, SearchInput } from "@/components/search-field";
import { UpsellModal } from "@/components/upgrade-modals/upsell-modal";
import { useGetBrandsList } from "@/features/brands/shared/hooks/useGetBrandsList";
// TeamLead tabs
import { LoadingIndicator } from "@/features/request/edit/components/chat-section/components/LoadingIndicator.tsx";
import { FeaturePageLayout } from "@/features/shared/layout/FeaturePageLayout";
import { useDebounce } from "@/hooks/useDebounce";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useUser } from "@/hooks/useUser";
import { isKickstarterAccount } from "@/utils/isKickstarterAccount";
import { isPausePlan } from "@/utils/isPausePlan";

import type { RequestFilterType } from "../types/filters";
import type { RequestCounts } from "../types/tables";
import type { DesignerFilterType } from "./DesignerDropdownFilter";
import type { TeamLeadFilterType } from "./TeamLeadDropdownFilter";

import {
  useActiveRequests,
  useAllRequests,
  useArchivedRequests,
  useCompletedRequests,
  useDraftRequests,
  usePausedRequests,
  useQueuedRequests,
  useRequestCounts,
  useSuperAdminRequestCounts,
} from "../hooks";
import { useDesignerRequestCounts } from "../hooks/designer";
import { useTeamLeadRequestCounts } from "../hooks/teamlead";
import { useShowBanners } from "../hooks/useShowBanners.tsx";
import {
  getCountsByRole,
  getDefaultTabByRole,
  isDesignerUser,
  isSuperAdminUser,
  isTeamLeaderUser,
  shouldShowFeature,
} from "../utils/roleUtils";
import { DesignerDropdownFilter } from "./DesignerDropdownFilter";
import { RequestFilters } from "./RequestFilters";
import { RequestStatistics } from "./RequestStatistics";
// Customer tabs
import {
  ActiveTab as CustomerActiveTab,
  AllTab as CustomerAllTab,
  ArchivedTab as CustomerArchivedTab,
  CompletedTab as CustomerCompletedTab,
  DraftsTab as CustomerDraftsTab,
  PausedTab as CustomerPausedTab,
  QueuedTab as CustomerQueuedTab,
} from "./tabs/customer";
// Designer tabs
import {
  AssignedToAnyTab as DesignerAssignedToAnyTab,
  AssignedToMeTab as DesignerAssignedToMeTab,
} from "./tabs/designer";
// SuperAdmin tabs
import {
  CompletedTab as SuperAdminCompletedTab,
  DueTodayTab as SuperAdminDueTodayTab,
  OpenTab as SuperAdminOpenTab,
  OverdueTab as SuperAdminOverdueTab,
  PausedTab as SuperAdminPausedTab,
  QueuedTab as SuperAdminQueuedTab,
  ReadyForReviewTab as SuperAdminReadyForReviewTab,
} from "./tabs/superadmin";
import {
  AllRequestsTab,
  AssignedToMeTab as TeamLeadAssignedToMeTab,
  TeamRequestsTab,
  UnassignedRequestsTab,
} from "./tabs/teamlead";
import { TeamLeadDropdownFilter } from "./TeamLeadDropdownFilter";

export function RequestsPage() {
  const { currentProject, data: userData } = useUser();
  const userRole = userData?.user?.roleName;
  const navigate = useNavigate();

  const isCollaborator = userRole === USER_ROLES.COLLABORATOR;

  const { data: brandsData } = useGetBrandsList({
    direction: "desc",
    enabled: isCollaborator,
    itemsPerPage: 1,
    page: 1,
    sortBy: "createdAt",
  });
  const hasBrands =
    !isCollaborator || (brandsData?.metadata?.totalItems ?? 0) > 0;

  // Tab transition state to prevent key conflicts
  const [isTabTransitioning, setIsTabTransitioning] = useState(false);
  const previousTabRef = useRef<string>();
  const pendingScrollToProcessingRef = useRef(false);

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [upsellModalType, setUpsellModalType] = useState<"draft">("draft");

  const search = useSearch({ strict: false }) as {
    filter?: RequestFilterType;
    scroll?: "in-queue";
    search?: string;
    tab?:
      | "active"
      | "all-requests"
      | "all"
      | "archived"
      | "assigned-to-any"
      | "assigned-to-me"
      | "completed"
      | "drafts"
      | "due-today"
      | "open"
      | "overdue"
      | "paused"
      | "queued"
      | "ready-for-review"
      | "team-requests"
      | "unassigned-requests";
  };
  const searchValue = search.search || "";

  // Default tab based on user role
  const currentTab = search.tab || getDefaultTabByRole(userRole);

  const requestsTitle = useMemo(() => {
    switch (userRole) {
      case USER_ROLES.SUPER_ADMIN: {
        const titles: Record<string, string> = {
          completed: "Requests - Completed",
          "due-today": "Requests - Due Today",
          open: "Requests - Open",
          overdue: "Requests - Overdue",
          paused: "Requests - Paused",
          queued: "Requests - Queued",
          "ready-for-review": "Requests - Ready for Review",
        };

        return titles[currentTab] ?? "Requests";
      }
      case USER_ROLES.TEAM_LEADER: {
        const titles: Record<string, string> = {
          "all-requests": "Requests - All requests",
          "assigned-to-me": "Requests - Assigned to me",
          "team-requests": "Requests - Team requests",
          "unassigned-requests": "Requests - Unassigned requests",
        };

        return titles[currentTab] ?? "Requests";
      }
      case USER_ROLES.DESIGNER: {
        const titles: Record<string, string> = {
          "assigned-to-any": "Requests - Assigned to any",
          "assigned-to-me": "Requests - Assigned to me",
        };

        return titles[currentTab] ?? "Requests";
      }
      default: {
        const titles: Record<string, string> = {
          active: "Requests - Active",
          all: "Requests - All",
          archived: "Requests - Archived",
          completed: "Requests - Completed",
          drafts: "Requests - Drafts",
          paused: "Requests - Paused",
          queued: "Requests - Queued",
        };

        return titles[currentTab] ?? "Requests";
      }
    }
  }, [currentTab, userRole]);

  usePageTitle(requestsTitle);

  // Handle tab transitions with guard
  useEffect(() => {
    if (
      previousTabRef.current !== undefined &&
      previousTabRef.current !== currentTab
    ) {
      setIsTabTransitioning(true);
      // Brief delay to allow cleanup and prevent key conflicts
      const timer = setTimeout(() => {
        setIsTabTransitioning(false);
      }, 100);

      return () => clearTimeout(timer);
    }
    previousTabRef.current = currentTab;
  }, [currentTab]);

  const bannerConfig = useShowBanners();

  // Check if user is kickstarter
  const isKickstarter = isKickstarterAccount(
    userData?.user.accountType ?? null,
  );

  const isPause = isPausePlan(currentProject?.accountType ?? null);
  const isPendingPause =
    !isPause &&
    currentProject?.downgradeProductIsPaused &&
    currentProject.downgradeTime &&
    new Date(currentProject.downgradeTime) > new Date();
  const pauseBannerState: "paused" | "pending" | null = isPause
    ? "paused"
    : isPendingPause
      ? "pending"
      : null;
  const userFirstName = userData?.user.name?.split(" ")[0] ?? "";

  // Per-tab filter state management - role-based initialization
  const [tabFilters, setTabFilters] = useState<
    Record<string, RequestFilterType>
  >(() => {
    const baseFilters: Record<string, RequestFilterType> = {};

    if (userRole === USER_ROLES.SUPER_ADMIN) {
      // SuperAdmin tabs
      baseFilters.open = "all";
      baseFilters.completed = "all";
      baseFilters.paused = "all";
      baseFilters["due-today"] = "all";
      baseFilters.overdue = "all";
      baseFilters["ready-for-review"] = "all";
      baseFilters.queued = "all";
    } else {
      // Customer tabs
      baseFilters.active = "all";
      baseFilters.all = "all";
      baseFilters.completed = "all";
      baseFilters.drafts = "all";
      baseFilters.queued = "all";
    }

    return baseFilters;
  });

  // Team Lead dropdown filter state (separate from tabFilters)
  const [teamLeadFilter, setTeamLeadFilter] =
    useState<TeamLeadFilterType>("open");

  // Designer dropdown filter state (separate from tabFilters)
  const [designerFilter, setDesignerFilter] =
    useState<DesignerFilterType>("open");

  // Get current filter for active tab, fallback to URL filter for backward compatibility
  const currentFilter = tabFilters[currentTab] || search.filter || "all";

  // Debounce search to prevent excessive API calls while typing
  const debouncedSearchValue = useDebounce(searchValue, 300);

  // Fetch customer counts only for customer users (skip for SuperAdmin, Team Lead, and Designer)
  const { data: requestCounts } = useRequestCounts({
    enabled:
      !isSuperAdminUser(userRole) &&
      !isTeamLeaderUser(userRole) &&
      !isDesignerUser(userRole),
  });

  // Fetch SuperAdmin counts only for SuperAdmin users
  const {
    data: unfilteredSuperAdminCounts,
    isLoading: isSuperAdminCountsLoading,
  } = useSuperAdminRequestCounts({
    enabled: isSuperAdminUser(userRole),
  });

  const { data: filteredSuperAdminCounts } = useSuperAdminRequestCounts({
    enabled: isSuperAdminUser(userRole) && !!debouncedSearchValue,
    search: debouncedSearchValue,
  });

  // Fetch Team Lead counts only for Team Lead users
  const { data: unfilteredTeamLeadCounts, isLoading: isTeamLeadCountsLoading } =
    useTeamLeadRequestCounts({
      enabled: isTeamLeaderUser(userRole),
    });

  const { data: filteredTeamLeadCounts } = useTeamLeadRequestCounts({
    enabled: isTeamLeaderUser(userRole) && !!debouncedSearchValue,
    search: debouncedSearchValue,
  });

  // Fetch Designer counts only for Designer users
  const { data: designerRequestCounts, isLoading: isDesignerCountsLoading } =
    useDesignerRequestCounts({
      enabled: isDesignerUser(userRole),
    });

  const getMergedTeamLeadCounts = useCallback(() => {
    if (!unfilteredTeamLeadCounts) {
      return;
    }

    if (!debouncedSearchValue || !filteredTeamLeadCounts) {
      return unfilteredTeamLeadCounts;
    }

    const isActiveTab = (tab: string) => currentTab === tab;

    return {
      counts: {
        allRequests: isActiveTab("all-requests")
          ? filteredTeamLeadCounts.counts.allRequests
          : unfilteredTeamLeadCounts.counts.allRequests,
        assignedToMe: isActiveTab("assigned-to-me")
          ? filteredTeamLeadCounts.counts.assignedToMe
          : unfilteredTeamLeadCounts.counts.assignedToMe,
        teamRequests: isActiveTab("team-requests")
          ? filteredTeamLeadCounts.counts.teamRequests
          : unfilteredTeamLeadCounts.counts.teamRequests,
        unassignedRequests: isActiveTab("unassigned-requests")
          ? filteredTeamLeadCounts.counts.unassignedRequests
          : unfilteredTeamLeadCounts.counts.unassignedRequests,
      },
    };
  }, [
    currentTab,
    debouncedSearchValue,
    filteredTeamLeadCounts,
    unfilteredTeamLeadCounts,
  ]);

  const teamLeadRequestCounts = getMergedTeamLeadCounts();

  const getMergedSuperAdminCounts = useCallback(() => {
    if (!unfilteredSuperAdminCounts) {
      return;
    }

    // When searching, return filtered counts for ALL tabs (not just active tab)
    if (debouncedSearchValue && filteredSuperAdminCounts) {
      return filteredSuperAdminCounts;
    }

    // When not searching, return unfiltered counts
    return unfilteredSuperAdminCounts;
  }, [
    debouncedSearchValue,
    filteredSuperAdminCounts,
    unfilteredSuperAdminCounts,
  ]);

  const superAdminRequestCounts = getMergedSuperAdminCounts();

  // Only fetch data for the current active tab to improve performance
  // Customer tab data (lazy loaded based on current tab AND role)
  // Only fetch customer-specific data for customer roles (Owner, Administrator, Collaborator)
  const isCustomerUser =
    !isSuperAdminUser(userRole) &&
    !isTeamLeaderUser(userRole) &&
    !isDesignerUser(userRole);

  const { data: allRequests } = useAllRequests({
    enabled:
      isCustomerUser && (currentTab === "all" || currentTab === "active"), // Enable for active as fallback
    filter: currentFilter,
    search: debouncedSearchValue,
  });
  const { data: draftRequests } = useDraftRequests({
    enabled: isCustomerUser && currentTab === "drafts",
    filter: currentFilter,
    search: debouncedSearchValue,
  });
  const { data: queuedRequests, isFetching: isQueuedFetching } =
    useQueuedRequests({
      enabled: isCustomerUser && currentTab === "queued",
      filter: currentFilter,
      search: debouncedSearchValue,
    });
  const { data: activeRequests } = useActiveRequests({
    enabled: isCustomerUser && currentTab === "active",
    filter: currentFilter,
    search: debouncedSearchValue,
  });
  const { data: completedRequests } = useCompletedRequests({
    enabled: isCustomerUser && currentTab === "completed",
    filter: currentFilter,
    search: debouncedSearchValue,
  });
  const { data: pausedRequests } = usePausedRequests({
    enabled: isCustomerUser && currentTab === "paused",
    filter: currentFilter,
    search: debouncedSearchValue,
  });
  const { data: archivedRequests } = useArchivedRequests({
    enabled: isCustomerUser && currentTab === "archived",
    filter: currentFilter,
    search: debouncedSearchValue,
  });

  // SuperAdmin tab data (lazy loaded based on current tab)
  const superAdminTabData = useMemo(
    () => ({
      currentTab,
      enabled: isSuperAdminUser(userRole),
      filter: currentFilter,
      search: debouncedSearchValue,
    }),
    [currentTab, currentFilter, debouncedSearchValue, userRole],
  );

  // Memoize tab handlers for performance
  const handleTabChangeCallback = useCallback(
    (key: Key) => {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          // Clear search when changing tabs
          search: undefined,
          tab: key.toString() as typeof search.tab,
        }),
        to: ".",
      });
    },
    [navigate],
  );

  const handleFilterChangeCallback = useCallback(
    (filter: RequestFilterType) => {
      // Update filter for current tab only - don't update URL or affect other tabs
      setTabFilters((prev) => ({
        ...prev,
        [currentTab]: filter,
      }));
    },
    [currentTab],
  );

  const handleTeamLeadFilterChangeCallback = useCallback(
    (filter: TeamLeadFilterType) => {
      setTeamLeadFilter(filter);
    },
    [],
  );

  const handleDesignerFilterChangeCallback = useCallback(
    (filter: DesignerFilterType) => {
      setDesignerFilter(filter);
    },
    [],
  );

  // Escalates a dead-end search to the "All requests" tab, keeping the search term (unlike manual tab changes). See MBNLC-1661.
  const handleTeamLeadResetToAllCallback = useCallback(() => {
    setTeamLeadFilter("all");
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: "all-requests" as typeof search.tab,
      }),
      to: ".",
    });
  }, [navigate]);

  // Same escalation for Designer: "Assigned to me" to "Assigned to any".
  const handleDesignerResetToAllCallback = useCallback(() => {
    setDesignerFilter("all");
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: "assigned-to-any" as typeof search.tab,
      }),
      to: ".",
    });
  }, [navigate]);

  // Same escalation for Customer: Active/Completed/Drafts/Queued to "All".
  const handleCustomerResetToAllCallback = useCallback(() => {
    setTabFilters((prev) => ({ ...prev, all: "all" }));
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: "all" as typeof search.tab,
      }),
      to: ".",
    });
  }, [navigate]);

  // Handle create button click - check kickstarter draft limit
  const handleCreateButtonClick = useCallback(() => {
    if (isKickstarter && requestCounts?.drafts && requestCounts.drafts >= 3) {
      setUpsellModalType("draft");
      setIsUpsellModalOpen(true);
    } else {
      navigate({ to: "/requests/pick-type" });
    }
  }, [isKickstarter, requestCounts?.drafts, navigate]);

  const handleUpgrade = useCallback(() => {
    setIsUpsellModalOpen(false);
    navigate({ to: "/pricing" });
  }, [navigate]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      navigate({
        replace: true,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          search: value || undefined,
        }),
        to: ".",
      });
    },
    [navigate],
  );

  const handleSearchClear = useCallback(() => {
    navigate({
      replace: true,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        search: undefined,
      }),
      to: ".",
    });
  }, [navigate]);

  useEffect(() => {
    if (search.scroll !== "in-queue") return;

    // Remove the parameter from URL to avoid scrolling on page refresh
    navigate({
      replace: true,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        scroll: undefined,
      }),
      to: ".",
    });

    const scrollToProcessing = (el: Element) => {
      pendingScrollToProcessingRef.current = false;
      el.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => {
        window.scrollBy({ behavior: "smooth", top: -80 });
      }, 500);
    };

    const existing = document.querySelector('[data-section="in-queue"]');

    if (existing) {
      scrollToProcessing(existing);

      return;
    }

    // Element not yet in DOM — watch for it and fall back to the data-load effect
    pendingScrollToProcessingRef.current = true;
    const observer = new MutationObserver(() => {
      const el = document.querySelector('[data-section="in-queue"]');

      if (el) {
        observer.disconnect();
        scrollToProcessing(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [search.scroll, navigate]);

  // Retry scroll once queued data finishes loading (covers slow-network case where
  // the MutationObserver fires before the list items render)
  useEffect(() => {
    if (!pendingScrollToProcessingRef.current || isQueuedFetching) return;
    const el = document.querySelector('[data-section="in-queue"]');

    if (el) {
      pendingScrollToProcessingRef.current = false;
      el.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => {
        window.scrollBy({ behavior: "smooth", top: -80 });
      }, 500);
    }
  }, [isQueuedFetching]);

  // Use dedicated count query for tab badges, fallback to data queries for current tab
  const counts = useMemo(() => {
    const baseCounts: RequestCounts = {
      active:
        requestCounts?.active ??
        (currentTab === "active"
          ? activeRequests?.total || activeRequests?.requests?.length || 0
          : 0),
      all:
        requestCounts?.all ??
        (currentTab === "all"
          ? allRequests?.total || allRequests?.requests?.length || 0
          : 0),
      archived:
        requestCounts?.archived ??
        (currentTab === "archived"
          ? archivedRequests?.total || archivedRequests?.requests?.length || 0
          : 0),
      completed:
        requestCounts?.completed ??
        (currentTab === "completed"
          ? completedRequests?.total || completedRequests?.requests?.length || 0
          : 0),
      drafts:
        requestCounts?.drafts ??
        (currentTab === "drafts"
          ? draftRequests?.total || draftRequests?.requests?.length || 0
          : 0),
      paused:
        requestCounts?.paused ??
        (currentTab === "paused"
          ? pausedRequests?.total || pausedRequests?.requests?.length || 0
          : 0),
      queued:
        requestCounts?.queued ??
        (currentTab === "queued"
          ? queuedRequests?.total || queuedRequests?.requests?.length || 0
          : 0),
    };

    // Use utility function to merge counts based on user role
    return getCountsByRole(userRole, baseCounts, superAdminRequestCounts);
  }, [
    requestCounts,
    superAdminRequestCounts,
    currentTab,
    activeRequests,
    allRequests,
    archivedRequests,
    completedRequests,
    draftRequests,
    pausedRequests,
    queuedRequests,
    userRole,
  ]);

  // Memoize tabs configuration based on user role
  const tabs = useMemo(() => {
    if (userRole === USER_ROLES.SUPER_ADMIN) {
      return [
        {
          className: "py-0",
          countLabel: counts["open"] || 0,
          id: "open",
          isLoading: isSuperAdminCountsLoading,
          label: "Open",
        },
        {
          className: "py-0",
          countLabel: counts["completed"] || 0,
          id: "completed",
          isLoading: isSuperAdminCountsLoading,
          label: "Completed",
        },
        {
          className: "py-0",
          countLabel: counts["paused"] || 0,
          id: "paused",
          isLoading: isSuperAdminCountsLoading,
          label: "Paused",
        },
        {
          className: "py-0",
          countLabel: counts["dueToday"] || 0,
          id: "due-today",
          isLoading: isSuperAdminCountsLoading,
          label: "Due Today",
        },
        {
          className: "py-0",
          countLabel: counts["overdue"] || 0,
          id: "overdue",
          isLoading: isSuperAdminCountsLoading,
          label: "Overdue",
        },
        {
          className: "py-0",
          countLabel: counts["readyForReview"] || 0,
          id: "ready-for-review",
          isLoading: isSuperAdminCountsLoading,
          label: "Ready for Review",
        },
        {
          className: "py-0",
          countLabel: counts["queued"] || 0,
          id: "queued",
          isLoading: isSuperAdminCountsLoading,
          label: "Queued",
        },
      ];
    }

    if (userRole === USER_ROLES.TEAM_LEADER) {
      const assignedToMeCount =
        teamLeadRequestCounts?.counts?.assignedToMe?.total ?? 0;
      const allRequestsCount =
        teamLeadRequestCounts?.counts?.allRequests?.total ?? 0;
      const unassignedRequestsCount =
        teamLeadRequestCounts?.counts?.unassignedRequests?.total ?? 0;

      return [
        {
          className: "py-0",
          countLabel: assignedToMeCount,
          id: "assigned-to-me",
          isLoading: isTeamLeadCountsLoading,
          label: "Assigned to me",
        },
        {
          className: "py-0",
          countLabel: allRequestsCount,
          id: "all-requests",
          isLoading: isTeamLeadCountsLoading,
          label: "All requests",
        },
        {
          className: "py-0",
          countLabel: teamLeadRequestCounts?.counts?.teamRequests?.total || 0,
          id: "team-requests",
          isLoading: isTeamLeadCountsLoading,
          label: "Team requests",
        },
        {
          className: "py-0",
          countLabel: unassignedRequestsCount,
          id: "unassigned-requests",
          isLoading: isTeamLeadCountsLoading,
          label: "Unassigned requests",
        },
      ];
    }

    if (userRole === USER_ROLES.DESIGNER) {
      // Designer tabs - use pre-calculated totals from backend
      return [
        {
          className: "py-0",
          countLabel: designerRequestCounts?.counts?.assignedToMe?.total || 0,
          id: "assigned-to-me",
          isLoading: isDesignerCountsLoading,
          label: "Assigned to me",
        },
        {
          className: "py-0",
          countLabel: designerRequestCounts?.counts?.assignedToAny?.total || 0,
          id: "assigned-to-any",
          isLoading: isDesignerCountsLoading,
          label: "Assigned to any",
        },
      ];
    }

    // Customer tabs
    return [
      {
        className: "py-0",
        countLabel: counts["drafts"],
        id: "drafts",
        label: "Drafts",
      },
      {
        className: "py-0",
        countLabel: counts["queued"],
        id: "queued",
        label: "Queued",
      },
      {
        className: "py-0",
        countLabel: counts["active"],
        id: "active",
        label: "Active",
      },
      {
        className: "py-0",
        countLabel: counts["completed"],
        id: "completed",
        label: "Completed",
      },
      {
        className: "py-0",
        countLabel: counts["paused"],
        id: "paused",
        label: "Paused",
      },
      {
        className: "py-0",
        countLabel: counts["archived"],
        id: "archived",
        label: "Archived",
      },
      {
        className: "py-0",
        countLabel: counts["all"],
        id: "all",
        label: "All",
      },
    ];
  }, [
    counts,
    designerRequestCounts,
    isDesignerCountsLoading,
    isSuperAdminCountsLoading,
    isTeamLeadCountsLoading,
    teamLeadRequestCounts,
    userRole,
  ]);

  // Memorize tab props to prevent unnecessary re-renders
  const tabProps = useMemo(
    () => ({
      // Pass lazy loading configuration for SuperAdmin tabs
      lazyLoadingConfig: superAdminTabData,
      onFilterChange: handleFilterChangeCallback,
      searchValue: debouncedSearchValue,
      selectedFilter: currentFilter,
    }),
    [
      debouncedSearchValue,
      currentFilter,
      superAdminTabData,
      handleFilterChangeCallback,
    ],
  );

  // Helper function to render the current tab content
  const renderTabContent = useCallback(() => {
    if (userRole === USER_ROLES.SUPER_ADMIN) {
      switch (currentTab) {
        case "open": {
          return <SuperAdminOpenTab {...tabProps} />;
        }
        case "completed": {
          return <SuperAdminCompletedTab {...tabProps} />;
        }
        case "paused": {
          return <SuperAdminPausedTab {...tabProps} />;
        }
        case "due-today": {
          return <SuperAdminDueTodayTab {...tabProps} />;
        }
        case "overdue": {
          return <SuperAdminOverdueTab {...tabProps} />;
        }
        case "ready-for-review": {
          return <SuperAdminReadyForReviewTab {...tabProps} />;
        }
        case "queued": {
          return <SuperAdminQueuedTab {...tabProps} />;
        }
        default: {
          return <SuperAdminOpenTab {...tabProps} />;
        }
      }
    }

    if (userRole === USER_ROLES.TEAM_LEADER) {
      // Team Lead tab content
      switch (currentTab) {
        case "all-requests": {
          return (
            <AllRequestsTab
              onFilterChange={handleTeamLeadFilterChangeCallback}
              searchValue={debouncedSearchValue}
              selectedFilter={teamLeadFilter}
            />
          );
        }
        case "team-requests": {
          return <TeamRequestsTab />;
        }
        case "unassigned-requests": {
          return (
            <UnassignedRequestsTab
              onFilterChange={handleTeamLeadResetToAllCallback}
              searchValue={debouncedSearchValue}
              selectedFilter={teamLeadFilter}
            />
          );
        }
        default: {
          // Default to "Assigned to me" tab for "assigned-to-me" and any other value
          return (
            <TeamLeadAssignedToMeTab
              onFilterChange={handleTeamLeadResetToAllCallback}
              searchValue={debouncedSearchValue}
              selectedFilter={teamLeadFilter}
            />
          );
        }
      }
    }

    if (userRole === USER_ROLES.DESIGNER) {
      // Designer tab content - default to "Assigned to me" tab
      if (currentTab === "assigned-to-any") {
        return (
          <DesignerAssignedToAnyTab
            onFilterChange={handleDesignerFilterChangeCallback}
            searchValue={debouncedSearchValue}
            selectedFilter={designerFilter}
          />
        );
      }

      return (
        <DesignerAssignedToMeTab
          onFilterChange={handleDesignerResetToAllCallback}
          searchValue={debouncedSearchValue}
          selectedFilter={designerFilter}
        />
      );
    }

    // Customer tab content. Non-"all" tabs escalate a dead-end search to "All"; "All" just resets its own category filter.
    switch (currentTab) {
      case "active": {
        return (
          <CustomerActiveTab
            {...tabProps}
            onFilterChange={handleCustomerResetToAllCallback}
          />
        );
      }
      case "archived": {
        return (
          <CustomerArchivedTab
            {...tabProps}
            onFilterChange={handleCustomerResetToAllCallback}
          />
        );
      }
      case "completed": {
        return (
          <CustomerCompletedTab
            {...tabProps}
            onFilterChange={handleCustomerResetToAllCallback}
          />
        );
      }
      case "drafts": {
        return (
          <CustomerDraftsTab
            {...tabProps}
            onFilterChange={handleCustomerResetToAllCallback}
          />
        );
      }
      case "paused": {
        return (
          <CustomerPausedTab
            {...tabProps}
            onFilterChange={handleCustomerResetToAllCallback}
          />
        );
      }
      case "queued": {
        return (
          <CustomerQueuedTab
            {...tabProps}
            onFilterChange={handleCustomerResetToAllCallback}
          />
        );
      }
      default: {
        return <CustomerAllTab {...tabProps} />;
      }
    }
  }, [
    currentTab,
    tabProps,
    userRole,
    debouncedSearchValue,
    teamLeadFilter,
    designerFilter,
    handleTeamLeadFilterChangeCallback,
    handleDesignerFilterChangeCallback,
    handleTeamLeadResetToAllCallback,
    handleDesignerResetToAllCallback,
    handleCustomerResetToAllCallback,
  ]);

  return (
    <div className="p-4 lg:p-xl overflow-x-hidden">
      <UpsellModal
        isOpen={isUpsellModalOpen}
        onOpenChange={setIsUpsellModalOpen}
        onUpgrade={handleUpgrade}
        type={upsellModalType}
      />
      <RequestsKickstarterBanner />
      {isCustomerUser && pauseBannerState && (
        <PauseBanner
          downgradeTime={currentProject?.downgradeTime ?? undefined}
          firstName={userFirstName}
          isOwner={userRole === USER_ROLES.OWNER}
          onUpgrade={() => {
            window.open("https://nlc.com/manage-my-account", "_blank");
          }}
          state={pauseBannerState}
        />
      )}
      {bannerConfig && (
        <div className="flex pb-4 justify-center">
          <Banner title={bannerConfig.title} variant="info">
            {bannerConfig.message}
          </Banner>
        </div>
      )}
      <FeaturePageLayout
        createButton={
          shouldShowFeature(userRole, "createButton")
            ? {
                href: "/requests/pick-type",
                isDisabled: !hasBrands,
                label: "New request",
                onClick: handleCreateButtonClick,
                requiredRoles: [
                  USER_ROLES.OWNER,
                  USER_ROLES.ADMIN,
                  USER_ROLES.COLLABORATOR,
                  USER_ROLES.TEAM_LEADER,
                  USER_ROLES.DESIGNER,
                ] as UserRole[],
              }
            : undefined
        }
        tabs={{
          className: "bg-bg-primary",
          onSelectionChange: handleTabChangeCallback,
          selectedKey: currentTab,
          tabs,
        }}
        title="Requests"
      >
        <div
          className={`flex flex-col items-stretch lg:flex-row lg:items-center ${currentTab === "team-requests" ? "" : "border-b border-border-secondary"}`}
        >
          {shouldShowFeature(userRole, "teamLeadDropdown") &&
            currentTab !== "team-requests" && (
              <TeamLeadDropdownFilter
                onFilterChange={handleTeamLeadFilterChangeCallback}
                selectedFilter={teamLeadFilter}
              />
            )}
          {shouldShowFeature(userRole, "designerDropdown") && (
            <DesignerDropdownFilter
              onFilterChange={handleDesignerFilterChangeCallback}
              selectedFilter={designerFilter}
            />
          )}
          {currentTab !== "team-requests" && (
            <div className="w-full px-4 py-2 lg:flex-1 lg:min-w-0 lg:px-5 lg:py-3">
              <SearchField aria-label="Search requests" className="w-full">
                <SearchInput
                  onChange={handleSearchChange}
                  onClear={handleSearchClear}
                  placeholder="Search..."
                  value={searchValue}
                />
              </SearchField>
            </div>
          )}
          <div className="flex flex-col w-full px-4 py-1 lg:contents">
            {shouldShowFeature(userRole, "filters") &&
              currentTab !== "queued" && (
                <div className="flex items-center py-2 lg:py-0 lg:pl-0 lg:pr-5">
                  <RequestFilters
                    onFilterChange={handleFilterChangeCallback}
                    selectedFilter={currentFilter}
                  />
                </div>
              )}
            {shouldShowFeature(userRole, "statistics") && (
              <div className="py-2 lg:py-0">
                <RequestStatistics onTabChange={handleTabChangeCallback} />
              </div>
            )}
          </div>
        </div>
        <div className="">
          {/* Tab Content */}
          {isTabTransitioning ? (
            <Loader className="h-64" />
          ) : (
            <div key={`tab-${currentTab}-${userRole}`}>
              <LoadingIndicator isVisible />
              {renderTabContent()}
            </div>
          )}
        </div>
      </FeaturePageLayout>
    </div>
  );
}
